import { css } from '@emotion/react';
import { Stack } from '@mui/material';
import { AgGridEvent, GetRowIdParams, GridPreDestroyedEvent, GridReadyEvent } from 'ag-grid-community';
import { getMonth, subMonths } from 'date-fns';
import { set } from 'lodash-es';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { AgTableWithBaseStyle } from '../../../../../components/AgTable/AgTable2';
import { FieldFactory } from '../../../../../components/Form/FieldFactory';
import { FieldLabel } from '../../../../../components/Form/FormComponents';
import { booleanField } from '../../../../../data-fields/CommonFields';
import {
  createCompanyFinancialsDataModel,
  ICompanyFinancialsDataModel,
  KpiPeriod,
  KpiSection,
} from '../../../../../data-models/company-financials.data-model';
import {
  IKPIDataValue,
  KPIRequestFrequency,
  KPIRequestPeriod,
  kpiRequestPeriodToKpiPeriod,
  periodFromGranularityAndFrequency,
} from '../../../../../data-models/kpi-requests.data-model';
import {
  IKPITableDataModel,
  IKPITemplateSectionDataModel,
} from '../../../../../data-models/kpi-template.data-model';
import { PersonaKey } from '../../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../../services/PermissionService';
import {
  gridAutoHeightCss,
  gridCenterHeaderCss,
  nonEditableCellClass,
  nonEditableCellCss,
} from '../../../../../theme/gridStyle';
import { compareBySortOrder } from '../../../../../util/comparators';
import { captureUnexpectedState } from '../../../../../util/SentryUtils';
import { IFormField } from '../../../../../view-models/form.view-model';
import { companyProfileFinancialsDefaultColDef } from '../../../../CompanyProfiles/commonGridColDefs';
import { autoGroupColumnDef } from '../../../../CompanyProfiles/components/Financials/components/FinancialsGrid2';
import {
  FinancialsGridRowData,
  financialsToRowData,
  getFinancialsColumnDefs,
  getFinancialsHeaderData,
  kpiNameColumnDefs,
  kpiRequestCellEditable,
} from '../../../../CompanyProfiles/components/Financials/components/FinancialsGrid2Defs';
import { allCompanyFinancialsStateLoadable } from '../../../../CompanyProfiles/state/CompanyFinancialsState';
import { getEachDateByPeriodAndFrequency } from '../../../../CompanyProfiles/utils/financialUtils';
import { generateKPITableColHeaderData, KPITableColHeaderData } from '../../../utils';
import { dateToPlainDateString, plainDateToZonedDateTime } from '../../../../../util/date-utilts';
import { transformValue } from './KPIAgGrid';
import { useAtomValue } from 'jotai';

const defaultColDef = {
  ...companyProfileFinancialsDefaultColDef,
  flex: 1,
  minWidth: 150,
};
const kpiGridCss = css`
  ${gridAutoHeightCss};
  ${gridCenterHeaderCss};
  ${nonEditableCellCss};
`;
export interface IKPIRowData {
  kpiId: number;
  formField: IFormField<unknown>;
  valuesByDate: Record<string, unknown>;
}

export interface IKPIGridSectionProps {
  companyId: number;
  companyFYEDateString: string;
  kpiRequestFrequency: KPIRequestFrequency;
  kpiRequestGranularity: KPIRequestFrequency | null;
  kpiRequestPeriod: string;
  kpiResponseData: IKPIDataValue[];
  onKPIGridDataUpdated: (section: KpiSection, data: IKPIDataValue[]) => void;
  section: IKPITemplateSectionDataModel;
  disabled?: boolean;
  showHistoricalData?: boolean;
}

export function KPIAgGrid2(props: IKPIGridSectionProps) {
  const [showHistoricalData, setShowHistoricalData] = useState(false);
  const allowHistoricalData = !PermissionService.get().hasPersona(PersonaKey.managedServices);

  return (
    <>
      {allowHistoricalData && (
        <ShowHistoricalDataToggle show={showHistoricalData} setShow={setShowHistoricalData} />
      )}
      <KPIAgGrid {...props} showHistoricalData={showHistoricalData} />
    </>
  );
}

function KPIAgGrid(props: IKPIGridSectionProps) {
  const {
    companyId,
    companyFYEDateString,
    disabled = false,
    kpiRequestFrequency,
    kpiRequestGranularity,
    kpiRequestPeriod,
    kpiResponseData,
    onKPIGridDataUpdated,
    section,
    showHistoricalData,
  } = props;
  const gridTemplateSettings = section.meta as IKPITableDataModel;

  const requestPeriod = periodFromGranularityAndFrequency(kpiRequestGranularity, kpiRequestFrequency);
  const period = kpiRequestPeriodToKpiPeriod[requestPeriod] as KpiPeriod;

  const formHeaderData = useMemo(() => {
    return generateKPITableColHeaderData({
      fyeDateString: companyFYEDateString,
      periodISODate: kpiRequestPeriod,
      nextPeriod: gridTemplateSettings.section === KpiSection.budget,
      kpiRequestFrequency,
      selectedFrequency: requestPeriod,
    }).map((hd) => ({
      ...hd,
      minWidth: 150,
      flex: 1,
    }));
  }, [
    companyFYEDateString,
    gridTemplateSettings.section,
    kpiRequestFrequency,
    kpiRequestPeriod,
    requestPeriod,
  ]);

  // OK to rely on BE returning a 403 for external users?
  const financialsLoadable = useAtomValue(allCompanyFinancialsStateLoadable(companyId));
  const financials = financialsLoadable.state === 'hasData' ? financialsLoadable.data : undefined;
  const financialsToShow = useMemo(() => {
    return (financials ?? []).filter((f) =>
      shouldShowFinancialItem({
        financial: f,
        section: gridTemplateSettings.section,
        period,
        requestPeriodIso: kpiRequestPeriod,
      })
    );
  }, [financials, gridTemplateSettings.section, kpiRequestPeriod, period]);

  const responseData = useMemo(() => {
    return responseDataToFinancialItems({
      companyId,
      kpiResponseData,
      kpiPeriod: requestPeriod,
    });
  }, [companyId, kpiResponseData, requestPeriod]);

  const columnDefs = useMemo(() => {
    return getKpiColumnDefs({
      companyFYEDateString,
      formHeaderData,
      financials: [...financialsToShow, ...responseData],
      period,
      section: gridTemplateSettings.section,
      kpiRequestPeriod,
      showHistoricalData: showHistoricalData ?? false,
      editable: !disabled,
    });
  }, [
    companyFYEDateString,
    disabled,
    financialsToShow,
    formHeaderData,
    gridTemplateSettings.section,
    kpiRequestPeriod,
    period,
    responseData,
    showHistoricalData,
  ]);

  const rowData = useMemo(() => {
    return financialsToRowData({
      financials: [...financialsToShow, ...responseData],
      include: new Set([gridTemplateSettings.section]),
      kpiMap: new Map(
        gridTemplateSettings.metrics.toSorted(compareBySortOrder).map((metric) => [metric.id, metric])
      ),
      kpiGroupMap: new Map(),
      period,
      readOnly: false,
    });
  }, [financialsToShow, gridTemplateSettings.metrics, gridTemplateSettings.section, period, responseData]);

  const onDataChange = useCallback(
    (event: AgGridEvent) => {
      const updatedRowData = event.api.getGridOption('rowData');
      if (!updatedRowData) {
        return;
      }
      const updatedResponseData = fromKPIGridRowData(
        gridTemplateSettings.section,
        updatedRowData,
        formHeaderData.map((hd) => hd.date)
      );

      onKPIGridDataUpdated(gridTemplateSettings.section, updatedResponseData);
    },
    [formHeaderData, gridTemplateSettings.section, onKPIGridDataUpdated]
  );

  function onGridReady(event: GridReadyEvent) {
    event.api.addEventListener('cellValueChanged', onDataChange);
    event.api.addEventListener('pasteEnd', onDataChange);
  }

  function onGridPreDestroyed(event: GridPreDestroyedEvent) {
    event.api.removeEventListener('cellValueChanged', onDataChange);
    event.api.removeEventListener('pasteEnd', onDataChange);
  }

  function getRowId(params: GetRowIdParams<IKPIRowData>) {
    return params.data.kpiId.toString();
  }

  return (
    <div css={kpiGridCss}>
      <AgTableWithBaseStyle
        autoGroupColumnDef={autoGroupColumnDef}
        columnDefs={columnDefs}
        domLayout={'autoHeight'}
        defaultColDef={defaultColDef}
        getRowId={getRowId}
        groupDefaultExpanded={-1}
        groupDisplayType='groupRows'
        onGridReady={onGridReady}
        onGridPreDestroyed={onGridPreDestroyed}
        autoSizeStrategy={{ type: 'fitCellContents', skipHeader: false, colIds: ['kpiName'] }}
        rowData={rowData}
        rowGroupPanelShow='never'
        suppressMovableColumns={true}
        suppressRowDrag={true}
        suppressContextMenu={true}
        suppressCsvExport
        suppressExcelExport
        suppressLastEmptyLineOnPaste
      />
    </div>
  );
}

function responseDataToFinancialItems({
  companyId,
  kpiResponseData,
  kpiPeriod,
}: {
  companyId: number;
  kpiResponseData: IKPIDataValue[];
  kpiPeriod: KPIRequestPeriod;
}): ICompanyFinancialsDataModel[] {
  return kpiResponseData.map((kpiResponseData) =>
    kpiResponseDataToFinancialItem({ companyId, kpiResponseData, kpiPeriod })
  );
}

export function kpiResponseDataToFinancialItem({
  companyId,
  kpiResponseData,
  kpiPeriod,
}: {
  companyId: number;
  kpiResponseData: IKPIDataValue;
  kpiPeriod: KPIRequestPeriod;
}): ICompanyFinancialsDataModel {
  const { kpiId, date, section, value } = kpiResponseData;
  const period = kpiRequestPeriodToKpiPeriod[kpiPeriod] as KpiPeriod;
  if (!period) {
    captureUnexpectedState('missing request period', { kpiPeriod });
  }
  return createCompanyFinancialsDataModel({
    companyId,
    kpiId,
    period,
    section,
    date,
    value: value as string,
  });
}

function fromKPIGridRowData(
  kpiSection: KpiSection,
  rowData: FinancialsGridRowData[],
  dates: string[]
): IKPIDataValue[] {
  const res: IKPIDataValue[] = [];
  dates.forEach((date) => {
    rowData.forEach((row) => {
      res.push(kpiGridRowDataToKpiResponseData(row, date, kpiSection));
    });
  });
  return res;
}

export function kpiGridRowDataToKpiResponseData(
  rowData: FinancialsGridRowData,
  date: string,
  section: KpiSection
): IKPIDataValue {
  return {
    kpiId: rowData.kpiId,
    date,
    // period is stored in the request object
    section,
    value: transformValue(rowData[date], rowData.formField.renderer),
  };
}

export function shouldShowFinancialItem({
  financial,
  section,
  period,
  requestPeriodIso,
}: {
  financial: ICompanyFinancialsDataModel;
  section: KpiSection;
  period: KpiPeriod;
  requestPeriodIso: string;
}) {
  return (
    financial.period === period &&
    financial.section === section &&
    (financial.section === KpiSection.actual
      ? plainDateToZonedDateTime(financial.date).getTime() -
          plainDateToZonedDateTime(requestPeriodIso).getTime() <
        0
      : plainDateToZonedDateTime(financial.date).getTime() -
          plainDateToZonedDateTime(requestPeriodIso).getTime() <=
        0) // assuming budget is always one period ahead of actual
  );
}

export function getKpiColumnDefs({
  companyFYEDateString,
  formHeaderData,
  financials,
  period,
  section,
  kpiRequestPeriod,
  showHistoricalData,
  editable = true,
}: {
  companyFYEDateString: string;
  formHeaderData: KPITableColHeaderData[];
  financials: ICompanyFinancialsDataModel[];
  period: KpiPeriod;
  section: KpiSection;
  kpiRequestPeriod: string;
  showHistoricalData: boolean;
  /** The whole grid: a disabled request is read-only whatever its cells hold. */
  editable?: boolean;
}) {
  let historicalDataEndDate = plainDateToZonedDateTime(kpiRequestPeriod); // budget is always one period ahead of actual
  if (section === KpiSection.actual) {
    if (period == KpiPeriod.month) {
      historicalDataEndDate = subMonths(historicalDataEndDate, 1);
    } else if (period === KpiPeriod.quarter) {
      historicalDataEndDate = subMonths(historicalDataEndDate, 3);
    } else if (period === KpiPeriod.year) {
      historicalDataEndDate = subMonths(historicalDataEndDate, 12);
    }
  }

  const fye = getMonth(companyFYEDateString) + 1;
  let nPeriods = 12; // monthly
  if (period === KpiPeriod.quarter) {
    nPeriods = 8;
  } else if (period === KpiPeriod.year) {
    nPeriods = 4;
  }

  const isoDates = getEachDateByPeriodAndFrequency({
    totalSelectedPeriods: nPeriods,
    frequency: period,
    fye,
    endDate: historicalDataEndDate,
  }).dates.map((date) => dateToPlainDateString(date));

  const historicalHeaderData = showHistoricalData
    ? getFinancialsHeaderData(fye, isoDates, period, {
        editable: false,
        cellClass: nonEditableCellClass,
        minWidth: 150,
        flex: 1,
      })
    : [];

  const financialsByKpiIdPeriodSectionDate = financials.reduce((acc, curr) => {
    set(acc, [curr.kpiId, curr.period, curr.section, curr.date], curr);
    return acc;
  }, {});
  return [
    {
      ...kpiNameColumnDefs,
      initialSort: null,
      pinned: 'left' as const,
      cellRendererParams: {
        namePath: 'kpiDisplayName',
        descriptionPath: 'formField.description',
      },
      field: 'kpiDisplayName',
      tooltipField: 'kpiDisplayName',
    },
    ...getFinancialsColumnDefs([...historicalHeaderData, ...formHeaderData], {
      enabled: editable,
      isCellEditable: kpiRequestCellEditable(financialsByKpiIdPeriodSectionDate),
    }),
  ];
}

export function ShowHistoricalDataToggle({
  show,
  setShow,
}: {
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
}) {
  const formField = booleanField({
    key: 'showHistoricalData',
    label: 'Show Historical Data',
  });
  const formProps = {
    onChange: (value: boolean) => {
      setShow(value);
    },
    value: show,
  } as ControllerRenderProps;
  return (
    <Stack direction='row' alignItems='center' gap={1} justifyContent='flex-end' mb='-0.5rem' mt='-1.5rem'>
      <FieldFactory formField={formField} formProps={formProps} />
      <FieldLabel field={formField} />
    </Stack>
  );
}
